import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import { StatBar, StorageModeFilter, useStorageModeFilter } from '~/components/common';
import { ILocationResponse, IRealtimeStatusResponse } from '~/utils/interface/analytics';

const Location = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ILocationResponse>();
  const [realtimeStatus, setRealtimeStatus] = useState<IRealtimeStatusResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const storageMode = useStorageModeFilter();

  useEffect(() => {
    Promise.all([analyticsApi.getLocation(storageMode), analyticsApi.getRealtimeStatus(storageMode)])
      .then(([locationRes, realtimeStatusRes]) => {
        setData(locationRes.data.data);
        setRealtimeStatus(realtimeStatusRes.data.data);
      })
      .catch(() => setError('pages.location.loadError'))
      .finally(() => setIsLoading(false));
  }, [storageMode]);

  const total = (data?.succeeded ?? 0) + (data?.failed ?? 0) || 1;
  const locationDisplayTotal =
    (realtimeStatus?.locationDisplay.succeeded ?? 0) + (realtimeStatus?.locationDisplay.failed ?? 0) || 1;
  const batteryDisplayTotal =
    (realtimeStatus?.batteryDisplay.succeeded ?? 0) + (realtimeStatus?.batteryDisplay.failed ?? 0) || 1;

  return (
    <div>
      <h1 className='text-xl font-semibold text-foreground'>{t('pages.location.title')}</h1>
      <p className='mt-1 text-sm text-muted-foreground'>{t('pages.location.description')}</p>
      <StorageModeFilter />

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{t(error)}</p>}

      {data && (
        <div className='mt-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
          <h2 className='text-base font-semibold text-foreground'>{t('pages.location.seniorLocationTitle')}</h2>
          <p className='text-sm text-muted-foreground'>
            {t('pages.location.seniorLocationDescription')}
          </p>
          <StatBar label={t('common.succeeded')} value={data.succeeded} total={total} />
          <StatBar label={t('common.failed')} value={data.failed} total={total} tone='destructive' />
        </div>
      )}

      {realtimeStatus && (
        <div className='mt-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-6'>
          <h2 className='text-base font-semibold text-foreground'>
            {t('pages.location.caregiverStatusTitle')}
          </h2>
          <p className='text-sm text-muted-foreground'>
            {t('pages.location.caregiverStatusDescription')}
          </p>
          <StatBar label={t('pages.location.realtimeStatusOpened')} value={realtimeStatus.opened} total={realtimeStatus.opened || 1} />
          <div className='flex flex-col gap-2'>
            <p className='text-sm font-medium text-foreground'>{t('pages.location.seniorLocationDisplayed')}</p>
            <StatBar label={t('common.succeeded')} value={realtimeStatus.locationDisplay.succeeded} total={locationDisplayTotal} />
            <StatBar
              label={t('common.failed')}
              value={realtimeStatus.locationDisplay.failed}
              total={locationDisplayTotal}
              tone='destructive'
            />
          </div>
          <div className='flex flex-col gap-2'>
            <p className='text-sm font-medium text-foreground'>{t('pages.location.seniorBatteryDisplayed')}</p>
            <StatBar label={t('common.succeeded')} value={realtimeStatus.batteryDisplay.succeeded} total={batteryDisplayTotal} />
            <StatBar
              label={t('common.failed')}
              value={realtimeStatus.batteryDisplay.failed}
              total={batteryDisplayTotal}
              tone='destructive'
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Location;
